import React, { useState, useEffect, useContext } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import unorm from 'unorm';
import { IoSearchOutline } from 'react-icons/io5';
import { ContextSearch } from '../../providers/ContextSearch';
import {
  SearchContainer,
  SearchTypeContainer,
  SearchStateContainer,
  CustomSelect,
  CustomForm,
  CustomInputGroup,
  SearchIconBox,
} from './styles';

const Search = ({
  data,
  fields,
  placeholder,
  setSearchResult,
  onSearchTextChange,
  pageName,
  states,
  types,
}) => {
  const { searchValues, setSearchValues } = useContext(ContextSearch);
  
  // Deriva os valores atuais diretamente do ContextSearch
  const currentSearchText = searchValues[pageName]?.text || '';
  const currentSelectedState = searchValues[pageName]?.state || '';
  const currentSelectedType = searchValues[pageName]?.type || '';

  const handleSearch = () => {
    if (!data || !Array.isArray(data)) {
      setSearchResult([]);
      return;
    }

    let filteredData = [...data];

    // Filtro por texto de busca
    if (currentSearchText && currentSearchText.trim()) {
      const normalizedSearchText = unorm
        .nfd(currentSearchText.trim())
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      filteredData = filteredData.filter((item) => {
        return fields.some((field) => {
          const fieldValue = field
            .split('.')
            .reduce((obj, prop) => obj?.[prop], item);

          if (!fieldValue) return false;

          const normalizedFieldValue = unorm
            .nfd(fieldValue.toString())
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();

          return normalizedFieldValue.includes(normalizedSearchText);
        });
      });
    }

    // Filtro por estado
    if (currentSelectedState && currentSelectedState !== '') {
      filteredData = filteredData.filter((item) => {
        // Verifica diferentes possíveis estruturas de endereço
        let itemState = null;
        
        if (item.address) {
          itemState = item.address.state || item.address.estado || item.address.uf || item.address.stateCode;
        }
        
        // Se não encontrou no address, verifica no nível raiz do item
        if (!itemState) {
          itemState = item.state || item.estado || item.uf || item.stateCode;
        }
        
        // Normaliza a comparação (remove espaços e converte para maiúsculas)
        const normalizedItemState = itemState ? itemState.toString().trim().toUpperCase() : '';
        const normalizedSelectedState = currentSelectedState.toString().trim().toUpperCase();
        
        return normalizedItemState === normalizedSelectedState;
      });
    }

    // Filtro por tipo
    if (currentSelectedType && currentSelectedType !== '' && currentSelectedType !== 0) {
      filteredData = filteredData.filter((item) => {
        if (!item.type) return false;
        
        const itemType = typeof item.type === 'object' ? item.type.id : item.type;
        return itemType === currentSelectedType;
      });
    }

    // Salva filtros no localStorage sempre
    const currentFilters = JSON.parse(localStorage.getItem('filters') || '{}');
    const updatedFilters = {
      ...currentFilters,
      [pageName]: {
        type: currentSelectedType,
        state: currentSelectedState,
        text: currentSearchText,
      },
    };
    
    localStorage.setItem('filters', JSON.stringify(updatedFilters));

    setSearchResult(filteredData);
  };

  useEffect(() => {
    handleSearch();
  }, [data, currentSearchText, currentSelectedState, currentSelectedType]);

  const handleSearchTextChange = (value) => {
    // Atualiza o ContextSearch com o novo texto
    setSearchValues(pageName, {
      text: value,
      state: currentSelectedState,
      type: currentSelectedType
    });
  };

  const handleStateChange = (value) => {
    // Atualiza o ContextSearch com o novo estado
    setSearchValues(pageName, {
      text: currentSearchText,
      state: value,
      type: currentSelectedType
    });
  };

  const handleTypeChange = (value) => {
    // Se o valor for 0 (item "Busque por tipo..."), converte para string vazia
    const numericValue = value === '' || value === '0' ? '' : Number(value);
    // Atualiza o ContextSearch com o novo tipo
    setSearchValues(pageName, {
      text: currentSearchText,
      state: currentSelectedState,
      type: numericValue
    });
  };

  return (
    <SearchContainer id='search-container'>
      <CustomForm id='search-box'>
        <CustomInputGroup id='search-input'>
          <SearchIconBox>
            <IoSearchOutline />
          </SearchIconBox>
          <Form.Control
            style={{ caretColor: 'auto' }}
            type='search'
            autoFocus
            placeholder={placeholder}
            value={currentSearchText}
            onChange={(event) => handleSearchTextChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
        </CustomInputGroup>
        {states && states.length > 0 && (
          <SearchStateContainer id='search-state-container'>
            <CustomSelect
              id='search-state-select'
              value={currentSelectedState}
              onChange={(e) => handleStateChange(e.target.value)}
            >
              {states.map((state) => (
                <option value={state.code} key={state.code}>
                  {state.name}
                </option>
              ))}
            </CustomSelect>
          </SearchStateContainer>
        )}
        {types && types.length > 0 && (
          <SearchTypeContainer id='search-type-container'>
            <CustomSelect
              id='search-type-select'
              value={currentSelectedType === 0 ? '' : (currentSelectedType || '')}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {types.map((type) => (
                <option value={type.id} key={type.id}>
                  {type.description}
                </option>
              ))}
            </CustomSelect>
          </SearchTypeContainer>
        )}
      </CustomForm>
    </SearchContainer>
  );
};

export default Search;
